import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import api from "../services/api";

export default function FreightForm() {
  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState({
    CompanyName: "",
    Date: "",
    RatePerContainer: "",
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");

  // ================= FETCH COMPANIES =================
  const fetchCompanies = async () => {
    try {
      const res = await api.get("/freight");
      setCompanies(res.data);
    } catch (err) {
      console.error("Fetch companies error:", err);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // ================= CREATE =================
  const handleCreate = async () => {
    if (!newCompany.CompanyName || !newCompany.Date) {
      alert("Please fill all fields");
      return;
    }

    try {
      await api.post("/freight", newCompany);
      setNewCompany({ CompanyName: "", Date: "", RatePerContainer: "" });
      fetchCompanies();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error creating company");
    }
  };

  // ================= UPDATE =================
  const handleUpdate = async () => {
    try {
      await api.put(`/freight/${selectedCompany.CompanyID}`, selectedCompany);
      setEditDialogOpen(false);
      fetchCompanies();
    } catch (err) {
      console.error(err);
    }
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this company?")) return;
    try {
      await api.delete(`/freight/${id}`);
      fetchCompanies();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Company Management
      </Typography>

      {/* ADD COMPANY */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6">Add New Company</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Company Name"
              value={newCompany.CompanyName}
              onChange={(e) =>
                setNewCompany({ ...newCompany, CompanyName: e.target.value })
              }
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Date"
              type="date"
              value={newCompany.Date}
              onChange={(e) =>
                setNewCompany({ ...newCompany, Date: e.target.value })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Freight rate ($ per container)"
              type="number"
              value={newCompany.RatePerContainer}
              onChange={(e) =>
                setNewCompany({ ...newCompany, RatePerContainer: e.target.value })
              }
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
              helperText="Used by the Container Profit report"
            />
          </Grid>
        </Grid>
        <Button variant="contained" sx={{ mt: 2 }} onClick={handleCreate}>
          Add Company
        </Button>
      </Paper>

      {/* COMPANY LIST */}
      {isMobile ? (
        companies.map((c) => (
          <Paper key={c.CompanyID} sx={{ p: 2, mb: 2 }}>
            <Typography><b>ID:</b> {c.CompanyID}</Typography>
            <Typography><b>Company Name:</b> {c.CompanyName}</Typography>
            <Typography><b>Date:</b> {c.Date}</Typography>
            <Typography><b>Freight rate:</b> ${Number(c.RatePerContainer || 0).toFixed(2)} / container</Typography>
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => { setSelectedCompany(c); setEditDialogOpen(true); }}>
                Edit
              </Button>
              <Button size="small" color="error" onClick={() => handleDelete(c.CompanyID)}>
                Delete
              </Button>
            </Box>
          </Paper>
        ))
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: "#eee" }}>
                {["ID", "Company Name", "Date", "Freight rate ($/container)", "Actions"].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.CompanyID}>
                  <TableCell>{c.CompanyID}</TableCell>
                  <TableCell>{c.CompanyName}</TableCell>
                  <TableCell>{c.Date}</TableCell>
                  <TableCell>${Number(c.RatePerContainer || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => { setSelectedCompany(c); setEditDialogOpen(true); }}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDelete(c.CompanyID)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <>
              <TextField
                fullWidth
                label="Company Name"
                value={selectedCompany.CompanyName}
                onChange={(e) =>
                  setSelectedCompany({ ...selectedCompany, CompanyName: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={selectedCompany.Date}
                onChange={(e) =>
                  setSelectedCompany({ ...selectedCompany, Date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Freight rate ($ per container)"
                type="number"
                value={selectedCompany.RatePerContainer ?? ""}
                onChange={(e) =>
                  setSelectedCompany({ ...selectedCompany, RatePerContainer: e.target.value })
                }
                inputProps={{ min: 0, step: "0.01" }}
                helperText="Used by the Container Profit report"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
